/**
 * useOemDupCheck — the ONE debounced advisory OEM duplicate lookup
 * (GET /products/check-oem), hoisted from ProductFormWizard S7.4 (the hoist
 * its own docblock anticipated) so the classification chunk edit runs the
 * SAME check (FIELD-SIGNOFF ROW 22, F4 §2-Fix5): 800ms debounce, 2-criteria
 * stand-down (同產品大類 + 同OEM NUMBER), skip-when-unchanged-from-saved on
 * edit seats, FAILS OPEN on error (ux-lens #6). Display + guard only — the
 * result must never gate a submit, and this hook owns NO OEM data-model
 * concerns (多國多OEM / UPC-QR stay owner-gated on #2438, Q3/Q4).
 */

import { useEffect, useReducer, useRef } from 'react';
import { useDebounce } from 'ahooks';
import { productApi } from '@/services/api/product.api';
import type { CheckOemMatch, CheckOemResponse } from '@/types/api/product';

/**
 * FOLLOW-UP DISCHARGED — the shape and the transport, together.
 *
 * This file used to declare `OemDupMatch` by hand and fetch through
 * `apiClient.get('/products/check-oem')` directly, with the note "hoist into
 * `src/types/api/product.ts` + `productApi.checkOem` once that file is free —
 * this slice does not own it". Both halves now exist: `CheckOemMatch` /
 * `CheckOemResponse` live in `@/types/api/product`, and `productApi.checkOem`
 * is the typed leg (a NAMED HOLE — no graph root; advisory, still on the mock
 * transport, and countable in the products-surface census precisely because it
 * is named). So the hand-copied structural twin is retired in favour of
 * ALIASES — the two names stay exported because the FSM, `linkableOemMatches`
 * and both consumers' Tables read better in this slice's own vocabulary, but
 * they are no longer a second declaration of the same shape.
 */
export type OemDupMatch = CheckOemMatch;
export type CheckOemClientResponse = CheckOemResponse;

export interface OemCheckState {
  status: 'IDLE' | 'VALIDATING' | 'MATCH' | 'CLEAR';
  oemNo: string | null;
  matches: OemDupMatch[];
}

export type OemCheckAction =
  | { type: 'TYPING' }
  | { type: 'START'; oemNo: string }
  | { type: 'MATCH'; oemNo: string; matches: OemDupMatch[] }
  | { type: 'CLEAR'; oemNo: string }
  | { type: 'RESET' }
  /**
   * check-oem error/timeout FAILS OPEN (ux-lens #6): back to IDLE — the
   * prompt is suppressed and submit stays unblocked. The dispatcher logs
   * the error; dup detection is advisory, never a gate.
   */
  | { type: 'FAIL_OPEN' };

export const oemCheckReducer = (state: OemCheckState, action: OemCheckAction): OemCheckState => {
  switch (action.type) {
    case 'TYPING':
      return { status: 'IDLE', oemNo: null, matches: [] };
    case 'START':
      return { status: 'VALIDATING', oemNo: action.oemNo, matches: [] };
    case 'MATCH':
      // Ignore stale responses for a different OEM number
      if (state.status === 'VALIDATING' && state.oemNo !== action.oemNo) return state;
      return { status: 'MATCH', oemNo: action.oemNo, matches: action.matches };
    case 'CLEAR':
      if (state.status === 'VALIDATING' && state.oemNo !== action.oemNo) return state;
      return { status: 'CLEAR', oemNo: action.oemNo, matches: [] };
    case 'RESET':
    case 'FAIL_OPEN':
      return { status: 'IDLE', oemNo: null, matches: [] };
    default:
      return state;
  }
};

/**
 * Matches offering 選擇式關聯 — only rows that already carry an SCN (the
 * board list spec: SCN、既有產品料號、OEM Number、國家/市場). Matches without
 * an SCN stay informational; linking them is the detail-page drawer's job.
 */
export const linkableOemMatches = (matches: OemDupMatch[]): OemDupMatch[] =>
  matches.filter((match) => Boolean(match.scn));

export type OemCheckDecision = { action: 'RESET' } | { action: 'START'; oemNo: string };

/**
 * Pure request/skip predicate over one settled input. The board's duplicate
 * key needs BOTH criteria (同產品大類 + 同OEM NUMBER) — stand down without a
 * category; on edit seats an OEM unchanged from the saved value (trimmed,
 * case-insensitive) never re-prompts. Exported for the model-layer vitest.
 */
export const decideOemCheck = (input: {
  settledOemNo: string | null | undefined;
  categoryId: string | null | undefined;
  savedOemNo?: string | null;
}): OemCheckDecision => {
  const oemNo = (input.settledOemNo ?? '').trim();
  if (!oemNo || !input.categoryId) return { action: 'RESET' };
  if (oemNo.toLowerCase() === (input.savedOemNo ?? '').trim().toLowerCase()) {
    return { action: 'RESET' };
  }
  return { action: 'START', oemNo };
};

export interface UseOemDupCheckOptions {
  /** Live (unsettled) OEM input value — the hook owns the 800ms debounce. */
  oemNo: string | null | undefined;
  /** 產品大類 criterion — the check stands down while absent. */
  categoryId: string | null | undefined;
  subsidiaryId?: string | null;
  /** Edit seats: the saved OEM — an unchanged value never re-checks. */
  savedOemNo?: string | null;
  /** Edit seats: the edited product is excluded from its own matches. */
  excludeId?: string | null;
  /** Master gate (e.g. only while a modal is open); default true. */
  enabled?: boolean;
}

export interface UseOemDupCheckResult {
  oemCheck: OemCheckState;
  /** The settled OEM value — consumers key follow-up effects off it. */
  settledOemNo: string | null | undefined;
}

export const useOemDupCheck = ({
  oemNo,
  categoryId,
  subsidiaryId,
  savedOemNo,
  excludeId,
  enabled = true,
}: UseOemDupCheckOptions): UseOemDupCheckResult => {
  const [oemCheck, dispatchOem] = useReducer(oemCheckReducer, {
    status: 'IDLE',
    oemNo: null,
    matches: [],
  });

  const debouncedOemNo = useDebounce(oemNo, { wait: 800 });

  // Request-time params travel via a ref so an in-flight check is neither
  // cancelled nor refired by a category/subsidiary edit (the wizard's
  // getValues-at-request-time behavior, preserved through the hoist).
  const requestParamsRef = useRef({ categoryId, subsidiaryId, excludeId });
  requestParamsRef.current = { categoryId, subsidiaryId, excludeId };

  useEffect(() => {
    if (oemNo !== debouncedOemNo) dispatchOem({ type: 'TYPING' });
  }, [oemNo, debouncedOemNo]);

  // Debounce settle → exactly one START per settled input, or a stand-down.
  useEffect(() => {
    if (!enabled) {
      dispatchOem({ type: 'RESET' });
      return;
    }
    const decision = decideOemCheck({ settledOemNo: debouncedOemNo, categoryId, savedOemNo });
    if (decision.action === 'START') {
      dispatchOem({ type: 'START', oemNo: decision.oemNo });
    } else {
      dispatchOem({ type: 'RESET' });
    }
  }, [enabled, debouncedOemNo, categoryId, savedOemNo]);

  useEffect(() => {
    if (oemCheck.status !== 'VALIDATING' || !oemCheck.oemNo) return;
    let cancelled = false;
    const checkedOemNo = oemCheck.oemNo;
    (async () => {
      try {
        const params = requestParamsRef.current;
        // Routed through the facade (`productApi.checkOem`) rather than the
        // axios instance: the products-surface census counts every transport
        // call site, and an unregistered one outside `src/services/api/` is a
        // bypass. The facade drops undefined keys with `definedOnly`, which is
        // what the axios params serializer did here — same query string.
        const data = await productApi.checkOem({
          oemNo: checkedOemNo,
          categoryId: params.categoryId || undefined,
          subsidiaryId: params.subsidiaryId || undefined,
          ...(params.excludeId ? { excludeId: params.excludeId } : {}),
        });
        if (cancelled) return;
        if (data.matches.length > 0) {
          dispatchOem({ type: 'MATCH', oemNo: checkedOemNo, matches: data.matches });
        } else {
          dispatchOem({ type: 'CLEAR', oemNo: checkedOemNo });
        }
      } catch (error) {
        // FAILS OPEN (ux-lens #6): prompt suppressed, submit unblocked, error
        // logged — the advisory check stands down and must never become a gate.
        if (!cancelled) {
          console.error('[check-oem] advisory OEM dup-check failed open:', error);
          dispatchOem({ type: 'FAIL_OPEN' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [oemCheck.status, oemCheck.oemNo]);

  return { oemCheck, settledOemNo: debouncedOemNo };
};

export default useOemDupCheck;
